import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {{ form: HTMLFormElement, heading: HTMLElement | undefined, success: HTMLElement, error: HTMLElement, input: HTMLInputElement, submitButton: HTMLButtonElement }} NotifyMeRefs
 */

/**
 * Back-in-stock notification form.
 * Integrates with Klaviyo's client-side back-in-stock subscriptions API.
 *
 * @extends {Component<NotifyMeRefs>}
 */
class NotifyMeComponent extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    this.#abortController = new AbortController();
    super.connectedCallback();
    const { signal } = this.#abortController;

    this.closest('.shopify-section, dialog, product-card')
      ?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    this.refs.form?.addEventListener('submit', this.#onSubmit, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    super.disconnectedCallback();
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    const { detail } = await event.promise;
    if (!detail?.resource) return;

    const { resource } = detail;
    const isSoldOut = this.#isSoldOut(resource);

    if (resource?.id) this.dataset.variantId = String(resource.id);

    this.toggleAttribute('hidden', !isSoldOut);
    this.#reset();
  };

  /**
   * @param {SubmitEvent} event
   */
  #onSubmit = async (event) => {
    event.preventDefault();

    const form = this.refs.form;
    if (!form?.checkValidity()) {
      form?.reportValidity();
      return;
    }

    const klaviyoKey = this.dataset.klaviyoKey;
    if (!klaviyoKey) {
      console.warn('[notify-me] Klaviyo public key not configured in theme settings.');
      return;
    }

    const email = this.refs.input?.value?.trim();
    const variantId = this.dataset.variantId;
    if (!email || !variantId) return;

    const submitBtn = this.refs.submitButton;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
    }

    try {
      const res = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(klaviyoKey)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2023-12-15',
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email },
                  },
                },
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${variantId}`,
                  },
                },
              },
            },
          }),
        }
      );

      if (res.ok || res.status === 202) {
        this.#showSuccess();
      } else {
        this.#showError();
      }
    } catch {
      this.#showError();
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-busy');
      }
    }
  };

  /**
   * @param {Record<string, unknown> | null} resource - Variant JSON from the productSelect event.
   * @returns {boolean}
   */
  #isSoldOut(resource) {
    if (!resource) return false;
    // Shopify's variant JSON in productSelect omits inventory_policy/inventory_quantity.
    // available=false + shopify-managed is a reliable sold-out signal for this context.
    if (resource.inventory_management === 'shopify' && !resource.available) return true;
    // Fallback for full variant objects (e.g. initial SSR hydration events).
    const invManaged = resource.inventory_management === 'shopify';
    const invDeny = resource.inventory_policy === 'deny';
    const invQty = /** @type {number} */ (resource.inventory_quantity) ?? 0;
    const qtyMin = /** @type {{ min?: number }} */ (resource.quantity_rule)?.min ?? 1;
    return invManaged && invDeny && (invQty <= 0 || qtyMin > invQty);
  }

  #showSuccess() {
    this.refs.heading?.setAttribute('hidden', '');
    this.refs.form?.setAttribute('hidden', '');
    this.refs.success?.removeAttribute('hidden');
    const error = this.refs.error;
    if (error) {
      error.setAttribute('hidden', '');
      error.textContent = '';
    }
  }

  #showError() {
    const error = this.refs.error;
    if (error) {
      error.textContent = this.dataset.errorText ?? '';
      error.removeAttribute('hidden');
    }
  }

  #reset() {
    this.refs.heading?.removeAttribute('hidden');
    const form = this.refs.form;
    if (form) {
      form.removeAttribute('hidden');
      form.reset();
    }
    this.refs.success?.setAttribute('hidden', '');
    const error = this.refs.error;
    if (error) {
      error.setAttribute('hidden', '');
      error.textContent = '';
    }
  }
}

if (!customElements.get('notify-me-component')) {
  customElements.define('notify-me-component', NotifyMeComponent);
}
